/**
 * The Parinaam Foundation mark.
 *
 * One component rather than an `<img>` in each of the three places it appears —
 * the staff banner, the sign-in card, and the public registration form. Those
 * three are the whole outside of the product, and when the file at
 * `public/logo.svg` is replaced they must all change together; three separate
 * tags is how one of them ends up still pointing at last year's mark.
 *
 * **Decorative by default.** In the banner and on the sign-in card the words
 * "Parinaam" sit beside it in real text, so announcing the image would make a
 * screen reader say the name twice. Where the mark is the *only* thing carrying
 * the name — the public form's header — pass `label`.
 *
 * The fallback matters more than it looks. `logo.svg` is served from `public/`,
 * not bundled, so a bad deploy or a stale cache can 404 it. Rather than leave a
 * broken-image glyph in the banner, `onError` swaps in a lettered mark, and the
 * page still looks deliberate.
 */

import { useState } from 'react';
import styles from './BrandLogo.module.css';

export interface BrandLogoProps {
  /**
   * How much room the mark gets.
   *
   *   `banner`  the 56px staff header.
   *   `card`    centred above a card — the public form, where the mark is the
   *             page's only identifier.
   *   `lockup`  the same size, left-aligned, for a card that pairs the mark
   *             with a heading beneath it rather than centring both.
   */
  size?: 'banner' | 'card' | 'lockup';
  /**
   * Accessible name. Omit where the name is already in the text beside it —
   * the mark is then hidden from assistive technology entirely.
   */
  label?: string;
  className?: string;
}

export function BrandLogo({ size = 'banner', label, className }: BrandLogoProps) {
  const [failed, setFailed] = useState(false);

  const classes = [styles.logo, styles[size], className].filter(Boolean).join(' ');

  if (failed) {
    return (
      <span
        className={[styles.fallback, styles[size], className].filter(Boolean).join(' ')}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
      >
        P
      </span>
    );
  }

  return (
    <img
      src="/logo.svg"
      alt={label ?? ''}
      aria-hidden={label ? undefined : true}
      className={classes}
      onError={() => setFailed(true)}
    />
  );
}
